import { getLocalBoardUrl } from '@canvas/frame-api-devloop/dev-ui-state';
import { isLocalEAPDevMode } from '@canvas/frame-api-devloop/is-dev-mode';
import {
  type ContentAPIFactory,
  type ContentWindowApi,
  type ContentAPI,
} from '@canvas/frame-api-types';

import { CanvasContentAPI, isFrameAPI } from '../canvas-frame-api.ts';

/**
 * Other content window APIs should inherit this class
 * This is purely just for the convenience of implementing shared logic across apis
 */
class BaseContentAPI implements ContentWindowApi {
  constructor(
    private readonly contentAPIFactory: ContentAPIFactory = (rawContentAPI) =>
      isFrameAPI(rawContentAPI) ? new CanvasContentAPI(rawContentAPI) : undefined,
  ) {}

  get title(): string {
    return '';
  }

  set title(value: string) {}

  get href(): string {
    return '';
  }

  get isTopLevel(): boolean {
    return false;
  }

  protected get parent(): Window | undefined {
    return undefined;
  }

  get contentAPI(): ContentAPI | undefined {
    /* oxlint-disable-next-line core/no-restricted-syntax */ // eslint-disable-next-line no-restricted-syntax
    const rawContentAPI = this.parent?.contentAPI;

    return this.contentAPIFactory(rawContentAPI);
  }

  dispatchKeyboardEvent(_event: KeyboardEvent): void {}

  reload() {
    this.parent?.location.reload();
  }

  assignHref(href: string) {
    this.parent?.location.assign(href);
  }
}

/**
 * Exposes `window` methods for a host frame initialised as a smart link embed.
 *
 * We don't have access to the `parent` when we are in a smart link, so
 * accessing `window.parent` across domains would throw an error.
 */
export class SmartLinkAPI extends BaseContentAPI {
  get title() {
    return globalThis.document.title;
  }

  set title(value: string) {
    globalThis.document.title = value;
  }

  get href() {
    return globalThis.location.href;
  }

  get isTopLevel() {
    return false;
  }

  get parent() {
    return window;
  }

  dispatchKeyboardEvent(_event: KeyboardEvent) {
    return;
  }
}

/**
 * Exposes `window` methods for a host frame initialised in normal EAP mode.
 */
export class NativeAPI extends BaseContentAPI implements ContentWindowApi {
  get title() {
    /* oxlint-disable-next-line core/no-restricted-syntax */ // eslint-disable-next-line no-restricted-syntax
    return globalThis.parent.document.title;
  }

  set title(value: string) {
    /* oxlint-disable-next-line core/no-restricted-syntax */ // eslint-disable-next-line no-restricted-syntax
    globalThis.parent.document.title = value;
  }

  get href() {
    if (isLocalEAPDevMode()) {
      const url = getLocalBoardUrl();
      if (url) {
        return url;
      }
    }

    /* oxlint-disable-next-line core/no-restricted-syntax */ // eslint-disable-next-line no-restricted-syntax
    return globalThis.parent.location.href;
  }

  get isTopLevel() {
    /* oxlint-disable-next-line core/no-restricted-syntax */ // eslint-disable-next-line no-restricted-syntax
    return globalThis.parent === window;
  }

  get parent() {
    /* oxlint-disable-next-line core/no-restricted-syntax */ // eslint-disable-next-line no-restricted-syntax
    return globalThis.parent;
  }

  dispatchKeyboardEvent(event: KeyboardEvent) {
    /* oxlint-disable-next-line core/no-restricted-syntax */ // eslint-disable-next-line no-restricted-syntax
    globalThis.parent.document.body.dispatchEvent(event);
  }
}

/**
 * Exposes `window` methods for a host frame initialised in online mode, used by
 * the Whiteboards VSCode extension.
 */
export class OnlineAPI extends BaseContentAPI implements ContentWindowApi {
  get title() {
    /* oxlint-disable-next-line core/no-restricted-syntax */ // eslint-disable-next-line no-restricted-syntax
    return globalThis.window.document.title;
  }

  set title(value: string) {
    /* oxlint-disable-next-line core/no-restricted-syntax */ // eslint-disable-next-line no-restricted-syntax
    globalThis.window.document.title = value;
  }

  get href() {
    if (isLocalEAPDevMode()) {
      const url = getLocalBoardUrl();
      if (url) {
        return url;
      }
    }

    /* oxlint-disable-next-line core/no-restricted-syntax */ // eslint-disable-next-line no-restricted-syntax
    return globalThis.window.location.href;
  }

  get isTopLevel() {
    /* oxlint-disable-next-line core/no-restricted-syntax */ // eslint-disable-next-line no-restricted-syntax
    return globalThis.window === window;
  }

  get parent() {
    /* oxlint-disable-next-line core/no-restricted-syntax */ // eslint-disable-next-line no-restricted-syntax
    return globalThis.window;
  }

  dispatchKeyboardEvent(event: KeyboardEvent) {
    /* oxlint-disable-next-line core/no-restricted-syntax */ // eslint-disable-next-line no-restricted-syntax
    globalThis.window.document.body.dispatchEvent(event);
  }
}

export class DefaultFrameApi extends NativeAPI {}
